/**
 * THE WARDROBE: every part and color a cogent's character can wear, as
 * `wardrobe.json` lists them (built by scripts/build-cogent-wardrobe.py from
 * the Polyworld chargen library). A look picks one item per category and a
 * color per palette. No three.js here, so a wardrobe UI can read it cheaply.
 */

import { once } from "./cache";
import { FACE_PARTS } from "./expressions";

export type RGB = [number, number, number];

/** What a cogent wears: an item id (or none) per category, palette colors, and per-category cloth tints. */
export type Look = {
  /** The preset it started from, if any. */
  preset?: string;
  /** Category key (Body, Face, Eyes, Hair, Chest, Leg, Foot, …) → item id, or null for none. */
  parts: Record<string, string | null>;
  colors: {
    /** A skin palette name, or a custom color. */
    skin: string | RGB;
    hair: string;
    eyes: string;
    hat: string;
  };
  /** Category key → a cloth color for that part. */
  tints: Record<string, RGB>;
};

export type PartShade = { node: string; primitive: number; shade: number };
export type CharacterAnimation = {
  name: string;
  file: string;
  duration: number;
  loop: boolean;
  hold?: boolean;
  next?: string;
};

export type WardrobeItem = {
  nodes: string[];
  attachmentRotation?: number[];
  attachmentPivot?: number[];
  id: string;
  name: string;
  files: string[];
  skinNodes?: string[];
  hairShades: string[];
  hatShades: string[];
  clothShades: string[];
  primitiveShades: {
    hairShades: PartShade[];
    hatShades: PartShade[];
    clothShades: PartShade[];
  };
  /** Nodes of other parts this one hides (boots hide feet). A node that items of several categories hide stays shown unless each of them is worn and hides it. */
  hides?: string[];
  texture?: string;
  /** Where the pupils are, so they take the eye color. */
  pupilMask?: string;
  /** Takes another palette's color whole ("hair": the brows). */
  tint?: string;
  /** A rigid part riding one bone (a quiver on the spine). */
  attachmentBone?: string;
};

export type Wardrobe = {
  revision: string;
  defaults: {
    skin: number;
    hairColor: string;
    pupilColor: string;
    hatColor: string;
    animation: string;
  };
  base: string[];
  animations: CharacterAnimation[];
  rig: string;
  clips: Record<string, string>;
  categories: {
    key: string;
    directory: string;
    default: string | null;
    items: WardrobeItem[];
  }[];
  palettes: Record<
    "skin" | "hair" | "eyes" | "hat",
    { name: string; rgb: RGB }[]
  >;
  presets: Look[];
};

// The presets a cogent is dressed in when it has no saved look, picked by its id.
const STARTERS = [
  "Arcanist",
  "Ranger",
  "Vanguard Knight",
  "Druid Warden",
  "Demon Hunter",
  "Crossbowman",
  "Warlock",
  "Berserker",
];

/**
 * The stand-in before sign-in: a plain villager, not anyone's cogent. The "Base" preset's body and face in a linen
 * tunic, brown trousers, ankle boots and a leather belt; nothing in its hands.
 */
export const VILLAGER: Look = {
  preset: "Villager",
  parts: {
    Body: "body/base",
    Face: "heads/base",
    Eyes: "eyes/02_focused",
    Mouth: "mouths/01_relaxed_smile",
    Nose: "noses/tiny",
    Ears: "ears/round",
    Brow: "eyebrows/01_soft_arch",
    Hair: "hair/01_french_crop",
    Beard: null,
    Earring: null,
    Eyewear: null,
    Headgear: null,
    Chest: "clothing/torsos/01_linen_tunic",
    Back: null,
    Hand: null,
    Leg: "clothing/pants/09_brown_trousers",
    Foot: "clothing/boots/13_strapped_ankle_boots",
    "Left hand": null,
    "Right hand": null,
    Jacket: null,
    Belt: "clothing/belts/simple_leather_belt",
    Suspenders: null,
  },
  colors: { skin: "White", hair: "Chestnut", eyes: "Gray", hat: "Red" },
  tints: {},
};

const loaded = new Map<string, Promise<Wardrobe>>();

// A failed read is not kept: the next character to mount reads it again.
const readOnce = (url: string): Promise<Wardrobe> =>
  once(loaded, url, () =>
    fetch(url).then((r) => {
      if (!r.ok) throw new Error(`${r.status} reading ${url}`);
      return r.json() as Promise<Wardrobe>;
    }),
  );

/** The whole wardrobe at `baseUrl` (read once per page): for the wardrobe UI, or a look the index lacks. */
export const loadWardrobe = (baseUrl: string) =>
  readOnce(`${baseUrl}wardrobe.json`);

/**
 * The wardrobe trimmed to what a character first wears (`wardrobe-index.json`: the starter presets, the villager,
 * every face), read once per page: a page that only shows the character never reads the whole one.
 */
export const loadWardrobeIndex = (baseUrl: string) =>
  readOnce(`${baseUrl}wardrobe-index.json`);

/** A wardrobe that has every part `look` wears: the index when it does, else the whole wardrobe. */
export async function wardrobeFor(
  baseUrl: string,
  look: Look,
): Promise<Wardrobe> {
  const index = await loadWardrobeIndex(baseUrl);
  const has = new Set(
    index.categories.flatMap((c) => c.items.map((i) => i.id)),
  );
  return Object.values(look.parts).every((id) => id === null || has.has(id))
    ? index
    : loadWardrobe(baseUrl);
}

/**
 * For a wardrobe UI: each category's options (an id to show and save, a
 * label), whether it can be left empty, the palettes and the presets.
 */
export async function listWardrobe(baseUrl: string) {
  const w = await loadWardrobe(baseUrl);
  return {
    categories: w.categories.map((c) => ({
      key: c.key,
      optional: c.default === null,
      // A face part's own decal (its eyes, mouth or brow, flat) is its picture as it is.
      options: c.items.map((i) => ({
        id: i.id,
        label: i.name,
        image:
          i.texture && FACE_PARTS.includes(c.key) ? baseUrl + i.texture : null,
      })),
    })),
    palettes: w.palettes,
    presets: w.presets.map((p) => ({ name: p.preset!, look: p })),
  };
}

/** The look a cogent wears until it has saved one: a starter preset picked by its id. */
// The character's files as bytes, fetched once per page and kept across client navigation, so a remount (or the
// three.js chunk arriving late) parses what is already here. A failed fetch is not kept.
const bytes = new Map<string, Promise<ArrayBuffer>>();

/** `url`'s bytes: the same request for every caller on the page. */
export const assetBytes = (url: string): Promise<ArrayBuffer> =>
  once(bytes, url, () =>
    fetch(url).then((r) => {
      if (!r.ok) throw new Error(`${r.status} reading ${url}`);
      return r.arrayBuffer();
    }),
  );

/**
 * Starts fetching what the character first draws: the rig, its idle clip first and then the others, and the parts
 * of `look` (null: the starter its id picks; undefined: not known yet, so none). Called as the widget mounts,
 * before three.js has loaded.
 */
export async function warmCharacter(
  baseUrl: string,
  look: Look | null | undefined,
  cogentId: string,
): Promise<void> {
  const index = await loadWardrobeIndex(baseUrl);
  const wearing =
    look === undefined ? null : (look ?? defaultLook(index, cogentId));
  const w = wearing ? await wardrobeFor(baseUrl, wearing) : index;
  const items = new Map(
    w.categories.flatMap((c) => c.items.map((i) => [i.id, i] as const)),
  );
  const files = [
    w.rig,
    w.clips.idle,
    ...Object.values(wearing?.parts ?? {}).flatMap((id) =>
      id ? (items.get(id)?.files ?? []) : [],
    ),
    ...Object.values(w.clips),
  ];
  await Promise.all(
    files.map((f) => assetBytes(baseUrl + f).catch(() => undefined)),
  );
}

/** What a look shows, as a key: its parts in a fixed order, its colors and tints; the preset it came from not. */
export function lookKey(look: Look): string {
  const sorted = <T>(o: Record<string, T>) =>
    Object.keys(o)
      .sort()
      .map((k) => [k, o[k]]);
  return JSON.stringify([
    sorted(look.parts),
    sorted(look.colors),
    sorted(look.tints),
  ]);
}

export function defaultLook(w: Wardrobe, cogentId: string): Look {
  let hash = 0;
  for (const ch of cogentId) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
  const name = cogentId ? STARTERS[hash % STARTERS.length] : "Arcanist";
  return w.presets.find((p) => p.preset === name)!;
}
